import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, Card, CardMedia, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { getRecentAnime } from '../../../services/animeApi';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface RecentAnimeItem {
  mal_id: number;
  title: string;
  title_japanese: string | null;
  image_url: string | null;
  status: number;
  start_date: string | null;
  end_date: string | null;
  updated_at: string;
  priority_date: string;
  score: number | null;
  sub_status: number[];
  episodes_watched: number | null;
}

interface RecentAnimeWidgetProps {
  username?: string;
  isPublic?: boolean;
}

const getCoverUrl = (imageUrl: string | null) => {
  if (!imageUrl) return '/image/no_image.png';
  return imageUrl;
};

const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  position: 'relative',
  mb: 0,
};

const getStatusIcon = (status: number, subStatus: number[]) => {
  const isRewatch = subStatus?.includes(1);
  switch (status) {
    case 1:
      return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    case 2:
      return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
    case 3:
      return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    default:
      return null;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffInDays === 0) return '今日';
  if (diffInDays === 1) return '昨日';
  if (diffInDays < 31) return `${diffInDays}日前`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}ヶ月前`;
  return `${Math.floor(diffInDays / 365)}年前`;
};

const getScoreColor = (score: number | null) => {
  if (score === null) return '#666666';
  if (score >= 4.5) return '#4CAF50';
  if (score >= 4.0) return '#8BC34A';
  if (score >= 3.5) return '#CDDC39';
  if (score >= 3.0) return '#FFEB3B';
  if (score >= 2.5) return '#FFC107';
  if (score >= 2.0) return '#FF9800';
  if (score >= 1.5) return '#FF5722';
  return '#F44336';
};

const renderSubStatusIcons = (subStatus: number[]) => {
  if (!subStatus || subStatus.length === 0) return null;
  const icons = [];
  if (subStatus.includes(1)) {
    icons.push(
      <Box key="rewatch" sx={{ background: 'linear-gradient(45deg, #f0f0f0d8, #bebebed2)', borderRadius: '4px', padding: '1px 3px', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.3 }}>
        <RefreshIcon sx={{ color: '#383838', fontSize: '0.7rem' }} />
      </Box>
    );
  }
  return icons.length > 0 ? <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>{icons}</Box> : null;
};

const RecentAnimeWidget: React.FC<RecentAnimeWidgetProps> = ({ username, isPublic = false }) => {
  const [recentAnime, setRecentAnime] = useState<RecentAnimeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getRecentAnime(username);
        setRecentAnime(response.data);
      } catch (error) {
        console.error('最近のアニメ取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [username]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>最近視聴したアニメ</Typography>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          {[...Array(isMdUp ? 10 : 8)].map((_, i) => (
            <Grid item xs={3} sm={3} md={2.4} key={i}>
              <Box sx={{ position: 'relative', paddingTop: '133%' }}>
                <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 1 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (recentAnime.length === 0) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>最近視聴したアニメ</Typography>
        <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">最近視聴したアニメがありません</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 1.5, sm: 2.5 } }}>
      <Typography variant="subtitle2" color="text.secondary" lineHeight={1} sx={{ mb: 2 }}>最近視聴したアニメ</Typography>
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        {recentAnime.slice(0, isMdUp ? 10 : 8).map((anime) => (
          <Grid item xs={3} sm={3} md={2.4} key={anime.mal_id}>
            <Box>
              <Link to={`/anime/${anime.mal_id}`} style={{ textDecoration: 'none' }}>
                <Card sx={{ position: 'relative', paddingTop: '133%', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1, transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.02)' } }}>
                  <CardMedia component="img" image={getCoverUrl(anime.image_url)} alt={anime.title_japanese || anime.title}
                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }} />
                  <Box sx={{ position: 'absolute', top: 6, left: 6, display: 'flex', flexDirection: 'column' }}>
                    {renderSubStatusIcons(anime.sub_status)}
                  </Box>
                </Card>
              </Link>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getStatusIcon(anime.status, anime.sub_status)}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}>
                    {formatDate(anime.priority_date)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'auto' }}>
                  {anime.score ? (
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, py: 0.25, px: 0.75, lineHeight: '1', borderRadius: 1, backgroundColor: `${getScoreColor(anime.score)}B3`, color: 'white', fontWeight: 500, minWidth: '28px', textAlign: 'center' }}>
                      {anime.score.toFixed(1)}
                    </Typography>
                  ) : (
                    <Box sx={{ width: '30px', height: { xs: '17px', sm: '18px' }, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: '10px', height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
                    </Box>
                  )}
                </Box>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', display: { xs: 'block', sm: 'none' }, mt: 0.3, textAlign: 'right' }}>
                {formatDate(anime.priority_date)}
              </Typography>

              {anime.episodes_watched != null && anime.episodes_watched > 0 && (
                <Typography variant="caption" sx={{ color: 'pulse.info', fontSize: { xs: '0.55rem', sm: '0.6rem' }, mt: { xs: 0, sm: 0.5 }, lineHeight: 1, display: 'block', textAlign: { xs: 'right', sm: 'left' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {`${anime.episodes_watched}話視聴`}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default RecentAnimeWidget;
